import type { AssistantSettingsDto } from "@/types/ai-assistant-admin";
import { CAP_SETTINGS_LIMITS } from "@/types/ai-assistant-usage";

/**
 * Validasi batas plafon AI (06c) — dipakai form admin dan server supaya
 * aturannya sama. Semua batas 0 = tanpa batas (mode pantau); bulanan harus
 * ≥ harian bila keduanya diisi.
 */

export type CapUnit = "tokens" | "minutes" | "usd";

export type CapNumberKey =
	| "chatTokenLimitMonthly"
	| "voiceMinutesLimitDaily"
	| "voiceMinutesLimitMonthly"
	| "chatUsdLimitDaily"
	| "chatUsdLimitMonthly"
	| "voiceUsdLimitDaily"
	| "voiceUsdLimitMonthly";
export type CapSettingsKey = CapNumberKey | "capWarnPercent";

const L = CAP_SETTINGS_LIMITS;
const USD_DECIMALS = 2;

export const CAP_LIMIT_RULES: Record<
	CapNumberKey,
	{ unit: CapUnit; max: number }
> = {
	chatTokenLimitMonthly: { unit: "tokens", max: L.tokensMax },
	voiceMinutesLimitDaily: { unit: "minutes", max: L.minutesMax },
	voiceMinutesLimitMonthly: { unit: "minutes", max: L.minutesMax },
	chatUsdLimitDaily: { unit: "usd", max: L.usdMax },
	chatUsdLimitMonthly: { unit: "usd", max: L.usdMax },
	voiceUsdLimitDaily: { unit: "usd", max: L.usdMax },
	voiceUsdLimitMonthly: { unit: "usd", max: L.usdMax },
};

/** Pasangan [harian, bulanan] yang harus konsisten. */
const DAILY_MONTHLY_PAIRS: ReadonlyArray<
	readonly [keyof AssistantSettingsDto, CapNumberKey]
> = [
	["dailyTokenLimitGlobal", "chatTokenLimitMonthly"],
	["voiceMinutesLimitDaily", "voiceMinutesLimitMonthly"],
	["chatUsdLimitDaily", "chatUsdLimitMonthly"],
	["voiceUsdLimitDaily", "voiceUsdLimitMonthly"],
];

function validNumber(v: number, unit: CapUnit, max: number): boolean {
	if (!Number.isFinite(v) || v < 0 || v > max) return false;
	if (unit !== "usd") return Number.isInteger(v);
	return Math.round(v * 10 ** USD_DECIMALS) / 10 ** USD_DECIMALS === v;
}

type CapInput = Pick<
	AssistantSettingsDto,
	CapSettingsKey | "dailyTokenLimitGlobal"
>;

/** Error per field plafon; objek kosong = valid. */
export function validateCapSettings(
	s: CapInput,
): Partial<Record<CapSettingsKey, string>> {
	const errors: Partial<Record<CapSettingsKey, string>> = {};
	for (const key of Object.keys(CAP_LIMIT_RULES) as CapNumberKey[]) {
		const { unit, max } = CAP_LIMIT_RULES[key];
		if (!validNumber(s[key], unit, max))
			errors[key] =
				unit === "usd"
					? `Harus 0–${max.toLocaleString("id-ID")} (maks. ${USD_DECIMALS} desimal)`
					: `Harus bilangan bulat 0–${max.toLocaleString("id-ID")}`;
	}
	const warn = s.capWarnPercent;
	if (
		!Number.isInteger(warn) ||
		warn < L.warnPercentMin ||
		warn > L.warnPercentMax
	)
		errors.capWarnPercent = `Harus bilangan bulat ${L.warnPercentMin}–${L.warnPercentMax}`;
	for (const [dailyKey, monthlyKey] of DAILY_MONTHLY_PAIRS) {
		const daily = s[dailyKey as keyof CapInput] as number;
		const monthly = s[monthlyKey];
		if (!errors[monthlyKey] && daily > 0 && monthly > 0 && monthly < daily)
			errors[monthlyKey] = "Batas bulanan tidak boleh lebih kecil dari harian";
	}
	return errors;
}
